import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import cn from 'classnames';
import { track } from '@amplitude/analytics-browser';
import axios from 'axios';
import { useMutation } from 'react-query';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import RoundIcon from 'components/common/RoundIcon';
import ProgressRing from 'components/common/ProgressRing';
import useStore from 'store/timeline';
import useGlobalStore from 'store/global';
import Button from 'components/common/Button';
import TextEditor, { TextEditorRef } from 'components/common/TextEditor';
import { POST_POLICY_URL, useMediaQuery } from 'common/utils';
import styles from './CommentBox.module.scss';
import { User } from 'common/interfaces/api';
import {
  BlackListUrl,
  checkPolicyCommentText,
  PolicyError,
  PolicyLevel,
} from 'service/post';
import debounce from 'common/utils/debounce';

export type CommentBoxProps = {
  // postId is needed only when isComment/isDialog/editMode === true
  postId?: number;
  isLocked?: boolean;
  lockMessage?: string;
  parentId?: number;
  isReplyBox?: boolean;
  setShowReplyBox?: (show: boolean) => void;
  mentionTarget?: User;
  initText?: string;
  callback?: () => void;
};
export default function CommentBox({
  postId,
  isLocked = false,
  lockMessage = '',
  parentId = null,
  isReplyBox = false,
  setShowReplyBox,
  mentionTarget,
  initText = '',
  callback,
}: CommentBoxProps): JSX.Element {
  const charLimit = 300;
  const selfRef = useRef<HTMLDivElement>(null);
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const refetchPostComments = useStore((state) => state.refetchPostComments);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [textPolicies, setTextPolicies] = useState<PolicyError[]>([]);
  const [blackUrls, setBlackUrls] = useState<BlackListUrl[]>([]);
  const usingTextPolices = useMemo(() => {
    return textPolicies.filter((po) => po.level === PolicyLevel.SuperHigh);
  }, [textPolicies]);
  const hasPolicyError = usingTextPolices.length > 0 || blackUrls.length > 0;
  const [postable, setPostable] = useState(false);
  const [isFocused, setFocused] = useState(false);
  // Text editor

  const [plainText, setPlainText] = useState('');
  const textLength = useMemo(() => plainText.length, [plainText]);
  const textEditorRef = useRef<TextEditorRef>();
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    setPostable(textLength > 0 && textLength <= charLimit);
  }, [textLength, charLimit]);

  useEffect(() => {
    if (textEditorRef.current) {
      const text = textEditorRef.current.getContent();
      if (text) {
        textEditorRef.current.reset();
      }
    }
  }, [postId]);

  const isInlineMode = !isFocused && textLength === 0 && !isReplyBox;

  const { mutate: checkPostText } = useMutation(
    async (plainText: string) => {
      return checkPolicyCommentText(plainText);
    },
    {
      onSuccess: (data) => {
        // only update when have different data
        const policies = data?.data?.words || [];
        const isSame =
          policies.length === textPolicies.length &&
          policies.every((pl) =>
            textPolicies.some((policy) => policy.name === pl.name)
          );
        if (!isSame) setTextPolicies(policies);
        const urls = data?.data?.urls || [];
        const isSameUrls =
          urls.length === blackUrls.length &&
          urls.every((blUrl) => blackUrls.some((url) => url.url === blUrl.url));
        if (!isSameUrls) setBlackUrls(urls);
        setIsProcessing(false);
      },
      onError: () => {
        setIsProcessing(false);
      },
    }
  );

  const debouncedCheckPostText = useMemo(
    () => debounce(checkPostText, 1000),
    [checkPostText]
  );

  useEffect(() => {
    setIsProcessing(true);
    debouncedCheckPostText(plainText);
  }, [plainText, debouncedCheckPostText]);

  // Make comment
  const { isLoading: isMakingComment, mutate: makeComment } = useMutation(
    async () => {
      let content = textEditorRef.current.getContent();
      const hasMention =
        mentionTarget && content.includes(`@${mentionTarget.name}`);
      if (hasMention) {
        content = content.replace(
          `@${mentionTarget.name}`,
          `@${mentionTarget.id}`
        );
      }
      await axios.post(`/posts/${postId}/comments`, {
        text: content.trim(),
        parent_id: parentId,
        mentions: hasMention ? [mentionTarget.id] : [],
      });
    },
    {
      onSuccess: () => {
        track('Send Comment');
        textEditorRef.current.reset();
        setShowReplyBox && setShowReplyBox(false);
        callback && callback();
        refetchPostComments(parentId);
        setIsProcessing(false);
      },
      onError: () => {
        setSnackbarMessage({ type: 'error', text: 'コメントが失敗しました' });
        setIsProcessing(false);
      },
    }
  );

  useEffect(() => {
    if (!textEditorRef.current) return;
    textEditorRef.current.forceRender();
  }, [textPolicies, blackUrls]);

  const handleMakeComment = () => {
    if (!currentUser) {
      showLogin({});
    } else {
      makeComment();
    }
  };

  const avatarEl = (
    <div className={styles.avatar}>
      <RoundIcon
        user={currentUser}
        width={35}
        height={35}
        hasRing
        emotion={currentUser?.emotion}
      />
    </div>
  );

  const handleTextAreaFocus = useCallback(() => setFocused(true), []);
  const handleClickAway = useCallback(() => setFocused(false), []);

  return (
    <>
      <div
        className={cn(styles.commentBox, { [styles.locked]: isLocked })}
        ref={selfRef}
      >
        {avatarEl}
        <ClickAwayListener onClickAway={handleClickAway}>
          <div
            className={cn(styles.inputBox, {
              [styles.expanded]: !isInlineMode,
            })}
          >
            <div className={styles.textInputWrapper}>
              <div className={styles.textInput}>
                <TextEditor
                  ref={textEditorRef}
                  setPlainText={setPlainText}
                  placeholder={isLocked ? lockMessage : 'コメントする'}
                  onFocus={handleTextAreaFocus}
                  blackUrls={blackUrls}
                  readOnly={isLocked}
                  textPolicies={usingTextPolices}
                  initialText={initText}
                  charLimit={charLimit}
                  mentionTarget={mentionTarget}
                />
              </div>
            </div>
            <div className={styles.footer}>
              <div
                className={cn(styles.rightSide, {
                  [styles.expanded]: !isInlineMode,
                })}
              >
                <div className={styles.progress}>
                  <ProgressRing
                    radius={22}
                    current={textLength}
                    total={charLimit}
                  />
                </div>
                {isReplyBox && (
                  <div style={{ marginRight: '8px' }}>
                    <Button
                      onClick={() => setShowReplyBox && setShowReplyBox(false)}
                      text="キャンセル"
                      rounded
                      verticalPadding={isMobile ? 5 : 6}
                      alternateColor
                    />
                  </div>
                )}
                <div className={styles.createBtn}>
                  {hasPolicyError && (
                    <div className={styles.alert}>
                      投稿ポリシーに違反している可能性があります。詳しくは
                      <a target="_blank" href={POST_POLICY_URL}>
                        投稿ポリシー
                      </a>
                      をご覧ください。
                    </div>
                  )}
                  <Button
                    disabled={!postable || isProcessing || hasPolicyError}
                    onClick={() => {
                      if (isProcessing) return;
                      handleMakeComment();
                    }}
                    text="投稿"
                    rounded
                    verticalPadding={isMobile ? 5 : 6}
                    isLoading={isMakingComment}
                  />
                </div>
              </div>
            </div>
          </div>
        </ClickAwayListener>
      </div>
    </>
  );
}
